import { Box, TextField, Typography } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { Controller, useFieldArray, useFormContext } from "react-hook-form"

import { MAX_ALLOWED_OPTIONS } from "../constants"
import type { PaymentTermsSchemaFormValues } from "../schemas/PaymentTermsSchema"

import AddPaymentOption from "./AddPaymentOption"
import AddPayRuleSchedule from "./AddPayRuleSchedule"

import { Button } from "@/components/common/Button"
import { FIELD_ERROR_CLASSES, TEXT_FIELD_STYLING_PROPS } from "@/constants/forms"
import { PaymentRuleEventGql } from "@/graphql/codegen/graphql"

const PaymentTermForm: FC = () => {
  const { control } = useFormContext<PaymentTermsSchemaFormValues>()
  const {
    fields: optionsFields,
    append: appendOptions,
    remove,
  } = useFieldArray<PaymentTermsSchemaFormValues>({
    control,
    name: "options",
  })

  const isAllowedToRemoveOption = optionsFields.length > 1

  const handleAddOptions = () => {
    appendOptions({
      parts: [
        {
          paymentRule: {
            daysAfter: "",
            event: PaymentRuleEventGql.PlacingOrder,
            percentage: "",
          },
          includeSalesDiscount: false,
          discount: {
            percentage: "",
            applicableIfWithinDays: "",
          },
        },
      ],
    })
  }

  return (
    <Box className='space-y-2.5 overflow-y-auto'>
      <Box className='rounded-md border border-solid border-gray-400 p-6'>
        <Box className='flex items-center justify-between gap-3'>
          <Box className='flex items-center gap-3'>
            <Typography variant='subtitle1' className='pb-4 text-gray-900'>
              Payment Term Name<span className='text-red-600'>*</span>
            </Typography>
          </Box>
        </Box>
        <Controller
          name='name'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Box className='relative w-1/3'>
              <TextField {...field} {...TEXT_FIELD_STYLING_PROPS} data-testid='paymentName' placeholder='-' />
              {error?.message && <p className={FIELD_ERROR_CLASSES}>{error.message}</p>}
            </Box>
          )}
        />
      </Box>

      <Box className='space-y-10 pb-10'>
        {optionsFields.map((option, index) => (
          <AddPaymentOption
            optionIndex={index}
            key={option.id}
            isAllowedToRemoveOption={isAllowedToRemoveOption}
            onRemove={(index) => remove(index)}
          />
        ))}
        <Box className='flex justify-end gap-3'>
          <Button
            color='inherit'
            appearance='outlined'
            className='self-end'
            startIcon={<AddOutlined />}
            disabled={optionsFields.length >= MAX_ALLOWED_OPTIONS}
            onClick={handleAddOptions}
          >
            Add Payment Option
          </Button>
        </Box>
        <AddPayRuleSchedule />
      </Box>
    </Box>
  )
}

export default PaymentTermForm
